import type { ClientEvent, EventBatch, EventResponse } from "../../types/tracking";
import { BATCH_INTERVAL_MS, MAX_QUEUE_SIZE, RETRY_MAX_AGE_MS, TRACKER_URL } from "./config";
import {
  getAttribution,
  getHeadlineAssignment,
  getSession,
  getVisitorId,
  tagEventDataWithHeadline,
} from "./identity";
import { trackerState } from "./state";

const queue: ClientEvent[] = [];
let retryQueue: EventBatch[] = [];
let returnVisitFired = false;
let idbAvailable = true;
let backgroundFlushArmed = false;

// The tracking modules are bundled TWICE: the standalone /tracking.js bundle
// (built by scripts/build-tracking.ts) is the only copy `initTracking()` ever
// runs on — it owns the 2s setInterval(flushBatch) + pagehide/visibilitychange
// sendBeacon. Components import trackFormStart/trackFormFieldFocus/
// trackFormFieldError/trackRegistrationSubmit/trackFormSubmit from
// "@/utils/tracking" in the APP bundle, a separate, never-initialized module
// instance whose `queue` had no flush timer and no unload handler — those
// events shipped only when a submit called flushNow(), so every form
// abandoner's events were silently lost. Arming a lazy background flush on
// first queueEvent() closes that gap for the uninitialized copy; it is a
// no-op in the already-initialized standalone copy.
function armBackgroundFlush(): void {
  if (backgroundFlushArmed || typeof window === "undefined") return;
  backgroundFlushArmed = true;
  setInterval(() => {
    flushBatch();
  }, BATCH_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushNow();
  });
  window.addEventListener("pagehide", () => {
    flushNow();
  });
}

export function queueEvent(type: string, data: Record<string, unknown> = {}): void {
  if (!trackerState.initialized) armBackgroundFlush();
  const eventData = tagEventDataWithHeadline(data, getHeadlineAssignment());
  queue.push({ type, ts: Date.now(), data: eventData });
  if (queue.length >= MAX_QUEUE_SIZE) {
    flushBatch();
  }
}

// Queue an event whose data is already headline-tagged and whose delivery the
// caller drives itself (the page-leave beacon tags a whole burst of events with
// one cookie read). Deliberately skips the lazy-arm above: the only caller runs
// in the initialized copy, which already has its flush timer.
export function pushEvent(event: ClientEvent): void {
  queue.push(event);
}

function buildBatch(): EventBatch | null {
  if (queue.length === 0) return null;

  const events = queue.splice(0);
  const session = getSession();
  const attribution = getAttribution();

  const batch: EventBatch = {
    visitor_id: getVisitorId(),
    session_id: session.id,
    page_url: window.location.href,
    events,
  };

  if ((window as { __lc_ab?: Record<string, string> }).__lc_ab) {
    batch.ab = (window as { __lc_ab?: Record<string, string> }).__lc_ab;
  }

  // The collector stamps referrer/yclid/gclid/fbclid/etext/ybaip/utm onto
  // EVERY row of the batch server-side (and its users.source_utm update is
  // idempotent), so attribution must ride on EVERY batch, not just a
  // session's first — a once-per-session dedup here left every later event
  // (form_start/form_submit/registration_submit/page_leave/...) with NULL
  // utm_* columns, which is what production showed for every visitor's
  // post-first-batch events, Yandex Direct traffic included.
  if (document.referrer) batch.referrer = document.referrer;
  if (attribution.yclid) batch.yclid = attribution.yclid;
  if (attribution.gclid) batch.gclid = attribution.gclid;
  if (attribution.fbclid) batch.fbclid = attribution.fbclid;
  if (attribution.etext) batch.etext = attribution.etext;
  if (attribution.ybaip) batch.ybaip = attribution.ybaip;
  if (attribution.utm) batch.utm = attribution.utm;

  return batch;
}

async function persistRetry(batch: EventBatch): Promise<void> {
  retryQueue.push(batch);
  if (retryQueue.length > MAX_QUEUE_SIZE) {
    retryQueue = retryQueue.slice(-MAX_QUEUE_SIZE);
  }

  if (!idbAvailable) return;

  try {
    const { set } = await import("idb-keyval");
    await set("_lc_retry_queue", retryQueue);
  } catch {
    idbAvailable = false;
  }
}

export async function loadRetryQueue(): Promise<void> {
  if (!idbAvailable) return;
  try {
    const { get } = await import("idb-keyval");
    const stored = await get<EventBatch[]>("_lc_retry_queue");
    if (Array.isArray(stored)) {
      const cutoff = Date.now() - RETRY_MAX_AGE_MS;
      retryQueue = stored.filter((b) => b.events.length > 0 && b.events[0].ts > cutoff);
    }
  } catch {
    idbAvailable = false;
  }
}

async function clearRetryQueue(): Promise<void> {
  retryQueue = [];
  if (!idbAvailable) return;
  try {
    const { del } = await import("idb-keyval");
    await del("_lc_retry_queue");
  } catch {
    idbAvailable = false;
  }
}

export function hasPendingRetries(): boolean {
  return retryQueue.length > 0;
}

export async function flushBatch(): Promise<void> {
  const batch = buildBatch();
  const pending = retryQueue.splice(0);
  if (batch) pending.push(batch);
  if (pending.length === 0) return;

  for (const item of pending) {
    try {
      const res = await fetch(TRACKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item),
        keepalive: true,
      });

      if (res.ok) {
        try {
          const data = (await res.json()) as EventResponse;
          trackerState.visitNumber = data.visit_number;
          if (trackerState.visitNumber > 1 && !returnVisitFired) {
            returnVisitFired = true;
            queueEvent("return_visit", { visit_number: trackerState.visitNumber });
          }
        } catch {}
        await clearRetryQueue();
      } else if (res.status !== 422) {
        await persistRetry(item);
      }
    } catch {
      await persistRetry(item);
    }
  }
}

// sendBeacon is purpose-built to survive navigation; it is the only delivery
// that reliably outlives an unload.
function beaconBatch(batch: EventBatch): boolean {
  try {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(
        TRACKER_URL,
        new Blob([JSON.stringify(batch)], { type: "application/json" }),
      )
    );
  } catch {
    return false;
  }
}

// Immediately ship whatever is queued, without waiting for the 2s batch timer.
// Conversion events (registration_submit / form_submit) are fired right before a
// redirect (the form navigates to a thank-you page or payment provider on
// success), so the queued event would otherwise be lost when the page unloads
// before the next scheduled flush. fetch + keepalive is the fallback when
// sendBeacon is unavailable or its queue is full.
export function flushNow(): void {
  if (typeof window === "undefined") return;
  const batch = buildBatch();
  if (!batch) return;
  if (beaconBatch(batch)) return;
  try {
    void fetch(TRACKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(batch),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

// The unload path: ship the queue via sendBeacon only. No keepalive-fetch
// fallback — the page is going away and the caller has already pushed its
// final events.
export function beaconQueuedBatch(): void {
  const batch = buildBatch();
  if (batch) beaconBatch(batch);
}
